import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import PropertyCard from "./PropertyCard";

import {
  fetchProperties,
  togglePropertyLike,
  togglePropertyBookmark,
} from "../api/realty";

// RealtyHomeScreenContent — /feed canonical home feed.
//
// Substrate: design/index.html #Csection (.content .container .row
// #content-section). Per design-map.md §3: "stack of self-contained
// cards" — no shared list-level state, no filtering, no sorting, no
// scope tabs. Each card is its own unit.
//
// Routing decision (R2-settled):
//   /feed → temporary realty home feed during R2 stabilization
//   /     → stays on ProfileScreen during R2
//   later → /  flips to feed
//
// Backend: PropertyViewSet.list with no extra params; the queryset
// already returns ACTIVE ∪ own per PropertyAccess + get_queryset.
// Default ordering is server-side -created_at.
//
// Ownership:
//   * Single fetch on mount. No filters, no sort, no pagination UI
//     (design has none on index.html — per design-map.md §3 / SR-10).
//   * Engagement state route-local; same shape as the /search
//     widget. No shared engagement primitive.

function RealtyHomeScreenContent() {
  const userInfo = useSelector(
    (state) => state.userLogin && state.userLogin.userInfo
  );
  const currentUserId =
    (userInfo && (userInfo.id || userInfo.public_id)) || null;

  // R8.7.4 — Stale-hint consumption removed. Producers
  // (RealtyDetailScreenContent / EditRealtyScreenContent) now emit a
  // success toast instead of a navigation-state hint, so this screen no
  // longer reads location.state.mayBeStale, no longer renders a stale
  // strip, and no longer needs the manual handleReload that lived
  // exclusively inside that strip. /feed's existing mount-fetch is the
  // canonical refresh path.

  const [results, setResults] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  const [likedIds, setLikedIds] = useState(new Set());
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [likingIds, setLikingIds] = useState(new Set());
  const [bookmarkingIds, setBookmarkingIds] = useState(new Set());
  const likingIdsRef = useRef(new Set());
  const bookmarkingIdsRef = useRef(new Set());
  const [engagementError, setEngagementError] = useState(null);

  // R5.3 — Share clipboard-copy state. Per-card "copied" indicator;
  // mirrors the R3.10A copy-link containment philosophy from
  // DetailActionsPanel.js. No timer registry, no cleanup ref — the
  // setTimeout callback uses the functional updater so a setState
  // after unmount is a silent no-op.
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchProperties()
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.results) ? data.results : []);
        setResults(rows);
        // Hydrate engagement Sets from authoritative backend state.
        // Sets remain route-local optimistic overlays for click-time
        // immediacy.
        setLikedIds(
          new Set(rows.filter((r) => r.liked_by_me).map((r) => r.id))
        );
        setBookmarkedIds(
          new Set(rows.filter((r) => r.bookmarked_by_me).map((r) => r.id))
        );
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleLike = async (property) => {
    if (!property || likingIdsRef.current.has(property.id)) return;
    likingIdsRef.current.add(property.id);
    setLikingIds(new Set(likingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyLike(property.id);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (data && data.liked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      likingIdsRef.current.delete(property.id);
      setLikingIds(new Set(likingIdsRef.current));
    }
  };

  const handleToggleBookmark = async (property) => {
    if (!property || bookmarkingIdsRef.current.has(property.id)) return;
    bookmarkingIdsRef.current.add(property.id);
    setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyBookmark(property.id);
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (data && data.bookmarked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      bookmarkingIdsRef.current.delete(property.id);
      setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    }
  };

  // R5.3 — Share is a surface-local clipboard side effect. Copies the
  // canonical property URL only; NO PropertyShare token, NO referral,
  // NO analytics, NO cross-surface sync. Failures (Clipboard API
  // unavailable / write rejected) are silent per STEP R5.3.7.
  const handleShareCopy = async (property) => {
    const url = `${window.location.origin}/realty/${property.id}`;
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== "function"
      ) {
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopiedId(property.id);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === property.id ? null : curr));
      }, 2000);
    } catch (_err) {
      /* silent — clipboard write failed */
    }
  };

  // Per-card menu — same shape as /search. Locally duplicated rather
  // than abstracted (R1 Risk-E + CLAUDE.md "No infrastructure
  // extraction"). Each surface owns its own ownership-aware action
  // shape.

  const buildMenu = (property) => {
    const ownerId =
      (property.created_by && property.created_by.id) ||
      (property.created_by && property.created_by.public_id);
    const isOwner =
      currentUserId !== null &&
      currentUserId !== undefined &&
      ownerId !== undefined &&
      String(ownerId) === String(currentUserId);

    if (isOwner) {
      return [
        {
          id: "share",
          label:
            copiedId === property.id
              ? "Посилання скопійовано"
              : "Поширити",
          icon: "fa-solid fa-share-nodes",
          kind: "button",
          // Keep the modal open so the inline label swap is visible.
          // User dismisses manually via backdrop / Escape.
          dismiss: false,
          onClick: () => handleShareCopy(property),
        },
        {
          id: "edit",
          label: "Редагувати",
          icon: "fa-solid fa-pen-to-square",
          kind: "link",
          // R6.1 — wired to the editRealty surface. Owner-gated at
          // the screen level via R3.9 ownership derivation.
          href: `/realty/${property.id}/edit`,
        },
      ];
    }

    // Viewer list-card menu: Share only. Follow/complaint live on
    // Property detail; list-card stubs were removed in ACTION-MENUS-UX-B1.
    return [
      {
        id: "share",
        label:
          copiedId === property.id
            ? "Посилання скопійовано"
            : "Поширити",
        icon: "fa-solid fa-share-nodes",
        kind: "button",
        // Keep the modal open so the inline label swap is visible.
        // User dismisses manually via backdrop / Escape.
        dismiss: false,
        onClick: () => handleShareCopy(property),
      },
    ];
  };

  return (
    <div className="container">
      {engagementError ? (
        <div className="feature-realty-list__engagement-error" role="alert">
          {engagementError}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-realty-list__state">
          <Spinner size="lg" label="Завантаження стрічки" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-realty-list__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити стрічку"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
          />
        </div>
      ) : results.length === 0 ? (
        <div className="feature-realty-list__state">
          <EmptyState
            icon="fa-solid fa-house"
            title="Поки що немає оголошень"
            description="Активні оголошення з'являться тут."
            size="md"
          />
        </div>
      ) : (
        // index.html canonical container is the single-column .row
        // #content-section. No .card-elem col wrappers — the home
        // feed is one card per row at all breakpoints, per
        // design-map.md §3 "stack of self-contained cards".
        <div className="row" id="content-section">
          {results.map((property) => {
            const detailHref = `/realty/${property.id}`;
            const ownerHandle =
              property.created_by &&
              (property.created_by.handle ||
                property.created_by.public_id);
            const ownerHref = ownerHandle ? `/user/${ownerHandle}` : null;
            return (
              <PropertyCard
                key={property.id}
                property={property}
                detailHref={detailHref}
                ownerHref={ownerHref}
                liked={likedIds.has(property.id)}
                bookmarked={bookmarkedIds.has(property.id)}
                likePending={likingIds.has(property.id)}
                bookmarkPending={bookmarkingIds.has(property.id)}
                onToggleLike={() => handleToggleLike(property)}
                onToggleBookmark={() => handleToggleBookmark(property)}
                menuItems={buildMenu(property)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RealtyHomeScreenContent;
